// Header-funnel filter popover. Type-aware controls:
//   text   → contains / equals / starts_with / ends_with / is_empty / is_not_empty
//   number → equals / gt / gte / lt / lte / between / is_empty / is_not_empty
//   date   → same as number, with date inputs
//   enum   → multi-checkbox 'in' selector

import { useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'

import type { ColumnDef } from '../types'
import {
  hasCheckboxValues, hasDropdown, hasRadioValues, inputType, listHeadingOf,
} from '../utils'
import type { FilterOp, FilterSpec, FilterValueKind } from './gridTypes'
import DateField from '../widgets/DateField'

interface Props {
  column: ColumnDef
  columnKey: string
  current: FilterSpec | null
  onApply: (f: FilterSpec) => void
  onClear: () => void
  onClose: () => void
  anchorRect: DOMRect | null
}

function detectKind(col: ColumnDef): FilterValueKind {
  if (hasDropdown(col) || hasRadioValues(col) || hasCheckboxValues(col)) return 'enum'
  const t = inputType(col)
  if (t === 'number') return 'number'
  if (t === 'date' || t === 'datetime-local') return 'date'
  return 'text'
}

function defaultOp(kind: FilterValueKind): FilterOp {
  if (kind === 'text') return 'contains'
  if (kind === 'enum') return 'in'
  return 'equals'
}

const TEXT_OPS: { op: FilterOp; label: string }[] = [
  { op: 'contains', label: 'contains' },
  { op: 'equals', label: 'equals' },
  { op: 'starts_with', label: 'starts with' },
  { op: 'ends_with', label: 'ends with' },
  { op: 'not_equals', label: 'is not' },
  { op: 'is_empty', label: 'is empty' },
  { op: 'is_not_empty', label: 'is not empty' },
]

const NUM_OPS: { op: FilterOp; label: string }[] = [
  { op: 'equals', label: '=' },
  { op: 'not_equals', label: '≠' },
  { op: 'gt', label: '>' },
  { op: 'gte', label: '≥' },
  { op: 'lt', label: '<' },
  { op: 'lte', label: '≤' },
  { op: 'between', label: 'between' },
  { op: 'is_empty', label: 'is empty' },
  { op: 'is_not_empty', label: 'is not empty' },
]

export default function ColumnFilterMenu({
  column, columnKey, current, onApply, onClear, onClose, anchorRect,
}: Props) {
  const kind = useMemo(() => detectKind(column), [column])
  const ops = kind === 'text' ? TEXT_OPS : NUM_OPS

  const [op, setOp] = useState<FilterOp>(current?.op ?? defaultOp(kind))
  const [value, setValue] = useState<string>(
    current?.value != null ? String(current.value) : '',
  )
  const [from, setFrom] = useState<string>(
    current?.from != null ? String(current.from) : '',
  )
  const [to, setTo] = useState<string>(
    current?.to != null ? String(current.to) : '',
  )
  const [enumPicks, setEnumPicks] = useState<string[]>(() => {
    if (current?.op === 'in' && Array.isArray(current.value)) {
      return (current.value as unknown[]).map(String)
    }
    return []
  })

  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (ref.current.contains(e.target as Node)) return
      onClose()
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [onClose])

  const apply = () => {
    if (kind === 'enum') {
      onApply({
        column: columnKey, kind, op: 'in',
        value: enumPicks,
      })
      return
    }
    if (op === 'is_empty' || op === 'is_not_empty') {
      onApply({ column: columnKey, kind, op })
      return
    }
    if (op === 'between') {
      onApply({ column: columnKey, kind, op, from, to })
      return
    }
    onApply({ column: columnKey, kind, op, value })
  }

  const inputTypeAttr = kind === 'number' ? 'number'
    : kind === 'date' ? (inputType(column) === 'datetime-local' ? 'datetime-local' : 'date')
    : 'text'
  const isDate = kind === 'date'

  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    top: anchorRect ? Math.min(anchorRect.bottom + 4, window.innerHeight - 240) : 80,
    left: anchorRect ? Math.max(8, Math.min(anchorRect.left, window.innerWidth - 290)) : 80,
    width: 280,
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    boxShadow: '0 14px 40px rgba(0,0,0,0.3)',
    zIndex: 2200,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    fontSize: '0.8425rem',
  }

  return (
    <div ref={ref} style={popoverStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: '0.8625rem' }}>
          Filter — {listHeadingOf(column)}
        </strong>
        <button
          onClick={onClose}
          className="btn btn-ghost btn-sm"
          style={{ padding: 2 }}
          title="Close"
        >
          <X size={12} />
        </button>
      </div>

      {kind === 'enum' ? (
        <EnumPicker column={column} picks={enumPicks} setPicks={setEnumPicks} />
      ) : (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Operator</span>
            <select
              value={op}
              onChange={(e) => setOp(e.target.value as FilterOp)}
              style={inputStyle}
            >
              {ops.map((o) => (
                <option key={o.op} value={o.op}>{o.label}</option>
              ))}
            </select>
          </label>

          {op === 'between' ? (
            <div style={{ display: 'flex', gap: 6 }}>
              {isDate ? (
                <DateField type={inputTypeAttr} value={from} onChange={setFrom}
                  style={{ ...inputStyle, flex: 1 }} compact />
              ) : (
                <input
                  type={inputTypeAttr}
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  placeholder="From"
                  style={{ ...inputStyle, flex: 1 }}
                  autoFocus
                />
              )}
              {isDate ? (
                <DateField type={inputTypeAttr} value={to} onChange={setTo}
                  style={{ ...inputStyle, flex: 1 }} compact />
              ) : (
                <input
                  type={inputTypeAttr}
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="To"
                  style={{ ...inputStyle, flex: 1 }}
                />
              )}
            </div>
          ) : (op === 'is_empty' || op === 'is_not_empty') ? null : isDate ? (
            // Filtering by a date is typed the same way it is entered — see
            // `widgets/DateField`.
            <DateField type={inputTypeAttr} value={value} onChange={setValue}
              style={inputStyle} compact />
          ) : (
            <input
              type={inputTypeAttr}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Value"
              style={inputStyle}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply() } }}
            />
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
        {current && (
          <button className="btn btn-ghost btn-sm" onClick={() => { onClear(); onClose() }}>
            Clear
          </button>
        )}
        <button className="btn btn-primary btn-sm" onClick={() => { apply(); onClose() }}>
          Apply
        </button>
      </div>
    </div>
  )
}

function EnumPicker({
  column, picks, setPicks,
}: {
  column: ColumnDef
  picks: string[]
  setPicks: (next: string[]) => void
}) {
  const values = column.edit_mask?.values || []
  const toggle = (code: string) => {
    if (picks.includes(code)) setPicks(picks.filter(c => c !== code))
    else setPicks([...picks, code])
  }
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      maxHeight: 220, overflow: 'auto',
      border: '1px solid var(--color-border)', borderRadius: 6,
      padding: '6px 8px',
    }}>
      {values.length === 0 && (
        <span style={{ color: 'var(--color-text-secondary)' }}>
          No options defined for this column.
        </span>
      )}
      {values.map((v) => {
        const code = String(v.data ?? '')
        const checked = picks.includes(code)
        return (
          <label key={code} style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(code)}
            />
            <span>{v.display ?? code}</span>
          </label>
        )
      })}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '4px 6px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 4,
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
  fontSize: '0.8425rem',
  outline: 'none',
}
